/**
 * 서버 canonicalJson/canonicalSha256(cogo-platform routes/projects.ts)의 포트.
 * 알고리즘 동등성은 고정 벡터 테스트로 잠근다 — 서버쪽 fixture와 같은 literal.
 * 알려진 잔여 위험: jsonb 숫자 표기 정규화(예: 1.0) — 설계 §7 참조.
 */
export type StudioBlockRowInput = {
  block_name?: string
  ui?: unknown
  flow?: unknown
  variables?: unknown
  events?: unknown
  datasource?: unknown
  manifest?: unknown
  semantic?: unknown
  sort_order?: number
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return JSON.stringify(value) as string
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('non-finite JSON number')
    return JSON.stringify(value) as string
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record)
      // Mirror JSON.stringify's object-property semantics: an `undefined`
      // value drops the key entirely rather than serializing it. Without
      // this, a block with no `root` (buildBlockRows leaves `ui.root`
      // `undefined` when neither the nested nor flat alias supplies one)
      // throws here — even though the request body sent over the wire
      // (JSON.stringify'd) and the server's post-round-trip view of the
      // persisted row never contain that key at all. Both sides must derive
      // the same hash from the same *serialized* shape, not from the
      // in-memory shape before it crosses the JSON boundary.
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`
  }
  throw new Error('value is not canonical JSON')
}

async function canonicalSha256(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonicalJson(value)),
  )
  return `sha256:${Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')}`
}

/** 서버 handleProjectBlocksReplace의 row 매핑(:1439-1454)을 복제한다. */
export function normalizeStudioBlockRows(
  blocks: StudioBlockRowInput[],
): Array<Record<string, unknown>> {
  return blocks.map((block, index) => ({
    block_name:
      typeof block.block_name === 'string' && block.block_name.trim()
        ? block.block_name.trim()
        : `block-${index + 1}`,
    ui: block.ui ?? {},
    flow: block.flow ?? {},
    variables: block.variables ?? {},
    events: block.events ?? {},
    datasource: block.datasource ?? {},
    manifest: block.manifest ?? {},
    semantic: block.semantic ?? null,
    sort_order: typeof block.sort_order === 'number' ? block.sort_order : index,
  }))
}

/** persisted-set 수신증(persisted.blocksHash) 대조용 expected 해시. */
export async function studioBlocksArtifactHash(
  blocks: StudioBlockRowInput[],
): Promise<string> {
  const rows = normalizeStudioBlockRows(blocks).sort((a, b) =>
    String(a.block_name) < String(b.block_name) ? -1 : 1,
  )
  return canonicalSha256(rows)
}
